import {
  Flex,
  Icon,
  mdBumps,
  Section,
  SectionContent,
  SectionDescription,
  SectionProps,
  SectionTitle,
  smBumps,
  TertiaryButton,
  Typo,
  useModal,
} from '@wrisk/ui-components'
import React, { FC, useCallback, useState } from 'react'

import { Policy, Proposal } from '../../../domain'
import { useErrorHandlingAsyncCallback } from '../../../hooks/errors'
import { TKey, useWriskTranslation } from '../../../infrastructure/internationalisation'
import { useProduct } from '../../product/productContext'
import { AutoRenewalModal } from './AutoRenewalModal'

export interface AutoRenewalSectionProps extends SectionProps {
  policyOrProposalId: string
  isInitiallyEnabled?: boolean
  proposal?: Proposal
  subheader?: string
  invokeApi: (
    id: string,
    autoRenewalEnabled: { enabled: boolean; reason?: string },
  ) => Promise<Policy | Proposal>
}

const tKey = TKey('components.auto-renewal-section')

export const AutoRenewalSection: FC<AutoRenewalSectionProps> = ({
  policyOrProposalId,
  isInitiallyEnabled,
  subheader,
  invokeApi,
  ...props
}) => {
  const {
    product: {
      renewal: { allowModifyAutoRenewal, autoRenewalOptions },
    },
  } = useProduct()
  const { t } = useWriskTranslation()
  const modal = useModal()

  const [autoRenewalEnabled, setAutoRenewalEnabled] = useState(isInitiallyEnabled ?? true)

  const icon = autoRenewalEnabled ? 'tick' : 'cross'

  const onUpdateCallback = useErrorHandlingAsyncCallback(async (enabled: boolean) => {
    await invokeApi(policyOrProposalId, { enabled })
    setAutoRenewalEnabled(enabled)
    modal.hide()
  })

  const onClick = useCallback(() => {
    return modal.show({
      content: (
        <AutoRenewalModal
          tKey={tKey}
          initialValue={autoRenewalEnabled}
          options={autoRenewalOptions}
          onChange={onUpdateCallback.execute}
          isLoading={onUpdateCallback.loading}
        />
      ),
    })
  }, [autoRenewalEnabled, modal, t, policyOrProposalId])

  const isEditButtonDisabled =
    !autoRenewalOptions || !Object.keys(autoRenewalOptions).length

  if (!allowModifyAutoRenewal) {
    return null
  }

  return (
    <Section width={1} {...props}>
      <SectionTitle>{t(tKey('header'))}</SectionTitle>
      <SectionDescription>{subheader ?? t(tKey('subheader'))}</SectionDescription>
      <SectionContent>
        <Flex variant='raised' gap={smBumps} width={1} p={mdBumps}>
          <Flex alignItems='flex-start' justifyContent='center' gap={smBumps}>
            <Icon icon={icon} />
            <Typo>{t(tKey('status', String(autoRenewalEnabled)))}</Typo>
          </Flex>
          <TertiaryButton
            disabled={isEditButtonDisabled}
            layout='small'
            onClick={onClick}
            data-testid='open-auto-renewal-modal'
          >
            {t(tKey('actions.edit'))}
          </TertiaryButton>
        </Flex>
      </SectionContent>
    </Section>
  )
}
